import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as api from '../../services/api';
import { labelColors } from '../../theme';
import {
  Criteria,
  Granularity,
  GroundTruthCoverageFilters,
  GroundTruthCoverageResponse,
  GroundTruthCoverageRow,
  LabelType,
  Purpose,
  User,
} from '../../types';
import SearchableSelect from '../common/SearchableSelect';
import SortableTable, { HeadCell } from '../common/SortableTable';
import Loading from '../Loading';

const GRANULARITIES: Granularity[] = ['sentence', 'paragraph', 'essay'];

const TYPE_BREAKDOWN_KEYS: LabelType[] = [
  'positive',
  'negative',
  'both',
  'uncertain',
  'unidentifiable',
  'no attempt',
];
const TYPE_SHORT_LABEL: Record<LabelType, string> = {
  positive: 'Pos',
  negative: 'Neg',
  both: 'Both',
  uncertain: 'Unc',
  unidentifiable: 'Unid',
  'no attempt': 'NA',
};

const formatDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const filterChipText = (
  filterId: string,
  value: unknown,
  criteriaById: Map<string, Criteria>,
  purposesById: Map<string, Purpose>,
  usersById: Map<string, User>
): string => {
  if (filterId === 'createdFrom' || filterId === 'createdTo') {
    return new Date(value as string).toLocaleDateString();
  }
  if (Array.isArray(value)) {
    if (filterId === 'criteriaId' && value.length === 1) {
      return criteriaById.get(value[0])?.name ?? value[0];
    }
    if (filterId === 'purposeId' && value.length === 1) {
      return purposesById.get(value[0])?.name ?? value[0];
    }
    if (filterId === 'createdBy' && value.length === 1) {
      const user = usersById.get(value[0]);
      return user ? `${user.firstName} ${user.lastName}` : value[0];
    }
    if (value.length === 1) return String(value[0]);
    return `${value.length} selected`;
  }
  return String(value);
};

const filterLabel = (filterId: string): string => {
  switch (filterId) {
    case 'criteriaId':
      return 'Criteria';
    case 'granularity':
      return 'Granularity';
    case 'purposeId':
      return 'Purpose';
    case 'createdFrom':
      return 'From';
    case 'createdTo':
      return 'To';
    case 'createdBy':
      return 'Labeller';
    default:
      return filterId;
  }
};

const TypeBreakdown = ({ row }: { row: GroundTruthCoverageRow }) => {
  if (row.labelCount === 0 && row.implicitNaCount === 0)
    return <Typography variant="body2">—</Typography>;
  const naColors = labelColors['no attempt'];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {TYPE_BREAKDOWN_KEYS.map(key => {
        const count = row.labelsByType[key] ?? 0;
        if (count === 0) return null;
        const colors = labelColors[key];
        return (
          <Tooltip key={key} title={key}>
            <Chip
              label={`${TYPE_SHORT_LABEL[key]} ${count}`}
              size="small"
              sx={{
                backgroundColor: colors.main,
                color: 'rgba(0, 0, 0, 0.87)',
                border: `1px solid ${colors.border}`,
                fontVariantNumeric: 'tabular-nums',
              }}
            />
          </Tooltip>
        );
      })}
      {row.implicitNaCount > 0 && (
        <Tooltip title="Implicit N/A: fragments unlabelled by labellers active in 2026 for this criterion + transcript.">
          <Chip
            label={`N/A ${row.implicitNaCount.toLocaleString()}`}
            size="small"
            sx={{
              backgroundColor: naColors.main,
              color: 'rgba(0, 0, 0, 0.87)',
              border: `1px dashed ${naColors.border}`,
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const PurposeChips = ({ row }: { row: GroundTruthCoverageRow }) => {
  if (row.purposes.length === 0) return <Typography variant="body2">—</Typography>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {row.purposes.map(p => (
        <Chip key={p.id} label={p.name} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

const GroundTruthCoverage = () => {
  const [filters, setFilters] = useState<GroundTruthCoverageFilters>({});
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [data, setData] = useState<GroundTruthCoverageResponse | null>(null);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const criteriaById = useMemo(() => new Map(criteria.map(c => [c.id, c])), [criteria]);
  const purposesById = useMemo(() => new Map(purposes.map(p => [p.id, p])), [purposes]);
  const usersById = useMemo(() => new Map(users.map(u => [u.id, u])), [users]);

  useEffect(() => {
    const load = async () => {
      try {
        const [criteriaData, purposeData, userData] = await Promise.all([
          api.fetchCriteria(),
          api.fetchPurposes(),
          api.fetchUsers(),
        ]);
        setCriteria(criteriaData);
        setPurposes(purposeData);
        setUsers(userData);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setError('Failed to load filter options');
      } finally {
        setIsLoadingFilters(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoadingData(true);
      setError(null);
      try {
        const response = await api.fetchGroundTruthCoverage(filters);
        if (!cancelled) setData(response);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load ground-truth coverage:', err);
          setError('Failed to load ground-truth coverage');
        }
      } finally {
        if (!cancelled) setIsLoadingData(false);
      }
    };
    const handle = window.setTimeout(load, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [filters]);

  const handleMultiChange = useCallback(
    <K extends keyof GroundTruthCoverageFilters>(key: K) =>
      (event: SelectChangeEvent<string[]>) => {
        const raw = event.target.value;
        const list = (Array.isArray(raw) ? raw : [raw]).filter((v): v is string => Boolean(v));
        setFilters(prev => {
          const next = { ...prev };
          if (list.length === 0) {
            delete next[key];
          } else {
            (next as Record<string, unknown>)[key] = list;
          }
          return next;
        });
      },
    []
  );

  const handleDateChange = useCallback(
    (key: 'createdFrom' | 'createdTo') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilters(prev => {
        const next = { ...prev };
        if (!value) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    []
  );

  const clearFilter = useCallback((key: string) => {
    setFilters(prev => {
      const next = { ...prev };
      delete (next as Record<string, unknown>)[key];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  const activeFilterEntries = useMemo(() => {
    return Object.entries(filters).filter(([, v]) => {
      if (Array.isArray(v)) return v.length > 0;
      return Boolean(v);
    });
  }, [filters]);

  const buildLabelsLink = useCallback(
    (criteriaId: string): string => {
      const params = new URLSearchParams();
      params.set('criteriaId', criteriaId);
      if (filters.purposeId && filters.purposeId.length > 0) {
        params.set('purposeId', filters.purposeId.join(','));
      }
      if (filters.createdBy && filters.createdBy.length > 0) {
        params.set('createdBy', filters.createdBy.join(','));
      }
      if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
      if (filters.createdTo) params.set('createdTo', filters.createdTo);
      return `/admin/labels?${params.toString()}`;
    },
    [filters]
  );

  const headCells: HeadCell<GroundTruthCoverageRow>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Criterion',
        numeric: false,
        sortable: true,
        getComparableValue: row => row.name,
        renderCell: row => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.granularity}
            </Typography>
          </Box>
        ),
        width: '20%',
      },
      {
        id: 'purposes',
        label: 'Purposes',
        numeric: false,
        sortable: true,
        getComparableValue: row => row.purposes.length,
        renderCell: row => <PurposeChips row={row} />,
        width: '14%',
      },
      {
        id: 'labelledTranscripts',
        label: 'Total transcripts with labels',
        numeric: true,
        sortable: true,
        getComparableValue: row => row.labelledTranscripts,
        renderCell: row => (
          <Tooltip title="Count of (transcript, labeller) pairs. A transcript labelled by N people contributes N.">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.labelledTranscripts.toLocaleString()}
            </Typography>
          </Tooltip>
        ),
        width: '10%',
      },
      {
        id: 'distinctTranscriptions',
        label: 'Unique transcripts with labels',
        numeric: true,
        sortable: true,
        getComparableValue: row => row.distinctTranscriptions,
        renderCell: row => (
          <Tooltip title="Distinct transcripts where this criterion has at least one active label.">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.distinctTranscriptions.toLocaleString()}
            </Typography>
          </Tooltip>
        ),
        width: '10%',
      },
      {
        id: 'avgLabellersPerTranscript',
        label: 'Avg labellers per transcript',
        numeric: true,
        sortable: true,
        getComparableValue: row =>
          row.distinctTranscriptions > 0 ? row.labelledTranscripts / row.distinctTranscriptions : 0,
        renderCell: row => {
          if (row.distinctTranscriptions === 0) return <Typography variant="body2">—</Typography>;
          const avg = row.labelledTranscripts / row.distinctTranscriptions;
          return (
            <Tooltip title="Total transcripts with labels ÷ Unique transcripts with labels.">
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {avg.toFixed(2)}
              </Typography>
            </Tooltip>
          );
        },
        width: '10%',
      },
      {
        id: 'labelCount',
        label: 'Total labels',
        numeric: true,
        sortable: true,
        getComparableValue: row => row.labelCount,
        renderCell: row => (
          <Tooltip title="Sum of explicit labels across all label types for this criterion.">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.labelCount.toLocaleString()}
            </Typography>
          </Tooltip>
        ),
        width: '8%',
      },
      {
        id: 'labelsByType',
        label: 'Labels by type',
        numeric: false,
        sortable: true,
        getComparableValue: row => row.labelCount + row.implicitNaCount,
        renderCell: row => <TypeBreakdown row={row} />,
        width: '19%',
      },
      {
        id: 'latestLabelDate',
        label: 'Latest label',
        numeric: false,
        sortable: true,
        getComparableValue: row =>
          row.latestLabelDate ? new Date(row.latestLabelDate).getTime() : 0,
        renderCell: row => (
          <Typography variant="body2">{formatDate(row.latestLabelDate)}</Typography>
        ),
        width: '5%',
      },
      {
        id: 'actions',
        label: '',
        numeric: false,
        sortable: false,
        renderCell: row => (
          <Tooltip title="Open this criterion in Labels with the current filters applied.">
            <IconButton
              component={RouterLink}
              to={buildLabelsLink(row.id)}
              size="small"
              aria-label={`View labels for ${row.name}`}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
        width: '4%',
      },
    ],
    [buildLabelsLink]
  );

  const renderMultiSelect = (
    id: string,
    label: string,
    selectedValues: string[],
    options: { value: string; label: string }[],
    onChange: (event: SelectChangeEvent<string[]>) => void,
    searchable: boolean
  ) => {
    const renderValue = (selected: unknown) => {
      const arr = (selected as string[]) ?? [];
      if (arr.length === 0) return <em>All</em>;
      if (arr.length === 1) {
        return options.find(opt => opt.value === arr[0])?.label ?? arr[0];
      }
      return `${arr.length} selected`;
    };
    if (searchable) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel id={`${id}-label`} shrink>
            {label}
          </InputLabel>
          <SearchableSelect
            labelId={`${id}-label`}
            id={id}
            multiple
            value={selectedValues}
            onChange={onChange as (event: SelectChangeEvent<string | string[]>) => void}
            input={<OutlinedInput label={label} notched />}
            renderValue={renderValue}
            displayEmpty
            options={options.map(opt => ({ value: opt.value, label: opt.label }))}
          />
        </FormControl>
      );
    }
    return (
      <FormControl fullWidth size="small">
        <InputLabel id={`${id}-label`} shrink>
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple
          value={selectedValues}
          onChange={onChange}
          input={<OutlinedInput label={label} notched />}
          renderValue={renderValue}
          displayEmpty
        >
          {options.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  if (isLoadingFilters) return <Loading />;

  const granularityOptions = GRANULARITIES.map(g => ({ value: g, label: g }));
  const criteriaOptions = [...criteria]
    .filter(c => !c.isArchived || (filters.criteriaId?.includes(c.id) ?? false))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(c => ({ value: c.id, label: c.isArchived ? `${c.name} (Archived)` : c.name }));
  const purposeOptions = [...purposes]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(p => ({ value: p.id, label: p.name }));
  const userOptions = [...users]
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
    .map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }));

  const totals = data?.totals;

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Labels overview
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {renderMultiSelect(
              'criteria-filter',
              'Criteria',
              filters.criteriaId ?? [],
              criteriaOptions,
              handleMultiChange('criteriaId'),
              true
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {renderMultiSelect(
              'purpose-filter',
              'Purpose',
              filters.purposeId ?? [],
              purposeOptions,
              handleMultiChange('purposeId'),
              true
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            {renderMultiSelect(
              'granularity-filter',
              'Granularity',
              filters.granularity ?? [],
              granularityOptions,
              handleMultiChange('granularity'),
              false
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {renderMultiSelect(
              'created-by-filter',
              'Labeller',
              filters.createdBy ?? [],
              userOptions,
              handleMultiChange('createdBy'),
              true
            )}
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created from"
              InputLabelProps={{ shrink: true }}
              value={(filters.createdFrom ?? '').slice(0, 10)}
              onChange={handleDateChange('createdFrom')}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created to"
              InputLabelProps={{ shrink: true }}
              value={(filters.createdTo ?? '').slice(0, 10)}
              onChange={handleDateChange('createdTo')}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={clearAll}
              disabled={activeFilterEntries.length === 0}
            >
              Clear all filters
            </Button>
          </Grid>
        </Grid>
        {activeFilterEntries.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Active filters:
            </Typography>
            {activeFilterEntries.map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => clearFilter(key)}
                label={`${filterLabel(key)}: ${filterChipText(
                  key,
                  value,
                  criteriaById,
                  purposesById,
                  usersById
                )}`}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Tooltip
            arrow
            followCursor
            title="Sum across visible criteria of (transcript, labeller) pairs with at least one active label. The same transcript labelled by N people for a criterion contributes N."
          >
            <Paper sx={{ p: 2, cursor: 'help' }}>
              <Typography variant="overline" color="text.secondary">
                Total transcripts with labels
              </Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {totals?.labelledTranscripts?.toLocaleString() ?? '—'}
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <Tooltip
            arrow
            followCursor
            title="Sum across visible criteria of distinct transcripts that have at least one active label. Multiple labellers on the same transcript count once per criterion."
          >
            <Paper sx={{ p: 2, cursor: 'help' }}>
              <Typography variant="overline" color="text.secondary">
                Unique transcripts with labels
              </Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {totals?.distinctTranscriptions?.toLocaleString() ?? '—'}
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={12} md={4}>
          <Tooltip
            arrow
            followCursor
            title="Total transcripts with labels ÷ Unique transcripts with labels."
          >
            <Paper sx={{ p: 2, cursor: 'help' }}>
              <Typography variant="overline" color="text.secondary">
                Avg labellers per transcript
              </Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {totals && totals.distinctTranscriptions > 0
                  ? (totals.labelledTranscripts / totals.distinctTranscriptions).toFixed(2)
                  : '—'}
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>

      {isLoadingData && !data ? (
        <Loading />
      ) : (
        <SortableTable
          data={data?.criteria ?? []}
          headCells={headCells}
          defaultSortBy="labelsByType"
          defaultOrder="desc"
          idAccessor={row => row.id}
          emptyMessage="No criteria match the current filters"
          stickyHeader
          pagination
          defaultRowsPerPage={50}
        />
      )}
    </Container>
  );
};

export default GroundTruthCoverage;
